﻿import {KeysetQuery, translation} from "@2d-front-monorepo/core";
import {ErrorMessage, KeysetPager, RemoteDataStatus, useRemoteData} from "@2d-front-monorepo/design-library";
import {
	formatDate,
	formatTime,
	ReservationId,
	ReservationResult, ReservationStatusComponent, Tariffs
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, useState} from "react";
import PermitsInlineLoading from "../../components/PermitsInlineLoading";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import {getHttpClient} from "../../hooks/getHttpClient";
import {ReservationTotalComponent} from "./ViewComponents/ReservationTotalComponent";

function Reservation({entry}: { entry: ReservationResult }): JSX.Element {
	const {installation} = getGlobalContext();
	const tariff = Tariffs.get(installation, entry.tariffId);

	return (
		<section className="card">
			<header className="layout horizontal aligned middle" id={"p_" + entry.id}>
				<div className="flex">
					<div className="grid">
						<dl className="permit__date col-1of2">
							<dt>{translation(PermitTranslations.start_date)}</dt>
							<dd>
								<span>{formatDate(entry.startDate.toDateOnly())}</span>
								<span>{formatTime(entry.startDate.toTimeOnly())}</span>
							</dd>
						</dl>
						<dl className="permit__date col-1of2">
							<dt>{translation(PermitTranslations.end_date)}</dt>
							<dd>
								<span>{formatDate(entry.endDate.toDateOnly())}</span>
								<span>{formatTime(entry.endDate.toTimeOnly())}</span>
							</dd>
						</dl>
					</div>
				</div>
				<ReservationStatusComponent reservation={entry}/>
			</header>
			<hr className="no-margin"/>
			<div className="grid padded">
				<dl className="title-desc col-1of2">
					<dt>{translation(PermitTranslations.reservation_tariff)}</dt>
					<dd>{tariff.title}</dd>
				</dl>
				<dl className="title-desc col-1of2">
					<dt>{translation(PermitTranslations.purpose)}</dt>
					<dd>{entry.purpose}</dd>
				</dl>

				<dl className="title-desc col-1of2">
					<dt>{translation(PermitTranslations.streets)}</dt>
					<dd>{entry.blocks.length}</dd>
				</dl>
				<dl className="title-desc col-1of2">
					<dt>{translation(PermitTranslations.meters)}</dt>
					<dd>{entry.meters.length}</dd>
				</dl>
			</div>
			<ReservationTotalComponent reservation={entry}/>
		</section>
	);
}

export default function ArchivedReservations(): JSX.Element {
	const [query, setQuery] = useState<KeysetQuery<ReservationId>>({});
	const {data, status, error} = useRemoteData(signal => getHttpClient().reservations.archived(query, signal), [query]);

	// Only the first load gets the spinner; paging keeps the previous cards on screen and
	// just dims them, so the tab does not empty out on every click.
	if (status === RemoteDataStatus.Loading && data == null) {
		return <PermitsInlineLoading/>;
	}

	if (status === RemoteDataStatus.Error) {
		return <ErrorMessage error={error}/>;
	}

	const reservations = data!.data;
	const loading = status === RemoteDataStatus.Loading;

	return (
		<div className={loading ? "layout--loading" : undefined}>
			<div className="layout vertical gap">
				{reservations.map(
					reservation => {
						return <Reservation key={reservation.id} entry={reservation}/>
					})
				}
			</div>
			<KeysetPager keyField={"id"} items={data!} onPageChange={setQuery}/>
		</div>
	);
}
